import React, { useMemo } from "react"
import { useRouter } from "next/router"

import { useAgentsList } from "@/agents/catalog"
import { useAlertsListTransport } from "@/alerts"
import { usePersonasList } from "@/persona"
import { useReportsListTransport } from "@/reports"
import { useTemplatesAll } from "@/templates/catalog"
import { useOrganization } from "@clerk/nextjs"

import { resolveAppCommand, type ResolvedAppCommand } from "@/lib/appCommands"
import { openCoviHome } from "@/lib/coviEvents"
import { dispatchOpenDebugInfo } from "@/lib/debugInfoEvents"
import { useCommandRegistry } from "@/contexts/CommandRegistryContext"
import { useKeyboardShortcuts } from "@/contexts/KeyboardShortcutsContext"
import type { PageShortcut } from "@/hooks/usePageShortcuts"
import { useQueryParams } from "@/hooks/useQueryParams"
import { useRestrictedAccess } from "@/hooks/useRestrictedAccess"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import {
  HOME_BASE_HREF,
  SOFIA_HOME_NAV_OVERRIDE,
} from "../config/siteNavigation"
import type { NavigationFeatureFlag } from "../types"
import {
  filterNavigationCommandTree,
  flattenNavigationCommandTree,
  NAVIGATION_COMMAND_TREE,
  type FlattenedNavigationCommandNode,
} from "../utils/navigationCommandTree"

function mapNavigationNodeToCommand(
  node: FlattenedNavigationCommandNode,
  navigateTo: (baseHref: string) => void,
): ResolvedAppCommand {
  return resolveAppCommand(
    {
      action: node.baseHref
        ? () => navigateTo(node.baseHref!)
        : () => undefined,
      aliases: node.pathLabels.map((label) => label.toLowerCase()),
      description: node.description,
      group: "navigate",
      id: node.id,
      isBranch: node.isBranch,
      key: node.key,
      keywords: [
        ...node.keywords,
        ...node.pathLabels.map((label) => label.toLowerCase()),
      ],
      label: node.label,
      parentId: node.parentId,
      scope: "global",
    },
    "navigation",
  )
}

export function useAvailableCommands(options: {
  enableEntityCommands?: boolean
  featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
}) {
  const { enableEntityCommands = false, featureFlagStatus } = options
  const { registeredCommands } = useCommandRegistry()
  const { getShortcuts } = useKeyboardShortcuts()
  const queryParams = useQueryParams()
  const router = useRouter()
  const { organization } = useOrganization()
  // The sidebar builds its hrefs from the active organization rather than the
  // route, which is why its rows stay organization-scoped on the global `/home`
  // page, where the URL carries no slug. A chord reaches the same destinations,
  // so it has to resolve them the same way — reading `router.query.slug` alone
  // sent every one of them to an organization-less path from that page.
  const orgSlug =
    (typeof router.query.slug === "string" ? router.query.slug : undefined) ??
    organization?.slug ??
    undefined
  const shouldLoadEntityCommands = enableEntityCommands && !!orgSlug
  const { canAccess } = useRestrictedAccess()
  const shortcuts = getShortcuts()
  const { data: agents = [] } = useAgentsList({
    enabled: shouldLoadEntityCommands,
  })
  const { data: personas = [] } = usePersonasList({
    enabled: shouldLoadEntityCommands,
  })
  const { data: templateData } = useTemplatesAll({
    enabled: shouldLoadEntityCommands,
  })
  const reportsQuery = useReportsListTransport({
    enabled: shouldLoadEntityCommands,
  })
  const alertsQuery = useAlertsListTransport({
    enabled: shouldLoadEntityCommands,
  })

  const navigateTo = React.useCallback(
    (baseHref: string) => {
      // Home is a Sofia surface like the sidebar tab: when a CoviProvider is
      // mounted it answers the open-home event and owns the navigation,
      // resuming the engaged thread. With no provider answering, the plain
      // route is the fallback.
      if (baseHref === HOME_BASE_HREF && openCoviHome()) {
        return
      }
      const href = queryParams.getHref(baseHref, orgSlug)
      void router.push(href)
    },
    [orgSlug, queryParams, router],
  )

  const visibleNavigationTree = useMemo(() => {
    const visible = filterNavigationCommandTree(NAVIGATION_COMMAND_TREE, {
      canAccess,
      featureFlagStatus,
    })
    // Home reads as "Sofia AI", so `g h` and the palette row match the
    // sidebar.
    // The node id stays keyed on the href, so shortcuts and active matching are
    // unaffected.
    return visible.map((node) =>
      node.baseHref === HOME_BASE_HREF
        ? {
            ...node,
            label: SOFIA_HOME_NAV_OVERRIDE.label,
            keywords: [...SOFIA_HOME_NAV_OVERRIDE.keywords],
          }
        : node,
    )
  }, [canAccess, featureFlagStatus])

  const flattenedNavigationNodes = useMemo(
    () => flattenNavigationCommandTree(visibleNavigationTree),
    [visibleNavigationTree],
  )

  const globalNavigationShortcuts = useMemo<PageShortcut[]>(
    () =>
      flattenedNavigationNodes
        .filter((node) => !!node.baseHref && !!node.key && !node.isBranch)
        .map((node) => ({
          action: () => navigateTo(node.baseHref!),
          command: {
            aliases: node.pathLabels.map((label) => label.toLowerCase()),
            description: node.description,
            group: "navigate",
            id: node.id,
            key: node.key,
            keywords: [
              ...node.keywords,
              ...node.pathLabels.map((label) => label.toLowerCase()),
            ],
            kind: "navigate",
            scope: "global",
          },
          key: node.key!,
          label: node.label,
          state: {
            hiddenInPalette: true,
          },
        })),
    [flattenedNavigationNodes, navigateTo],
  )

  const navigationCommands = useMemo(
    () =>
      flattenedNavigationNodes.map((node) =>
        mapNavigationNodeToCommand(node, navigateTo),
      ),
    [flattenedNavigationNodes, navigateTo],
  )

  const entityCommands = useMemo(() => {
    if (!shouldLoadEntityCommands) {
      return [] as ResolvedAppCommand[]
    }

    const templates = templateData?.templates ?? []
    const reports = reportsQuery.data ?? []
    const alertList = alertsQuery.data?.alerts ?? []
    const alerts = alertList.map((alert) => ({
      id: alert.id,
      name: String(stringifyUnknownValue(alert.name ?? alert.id)),
    }))

    return [
      ...agents.map((agent) =>
        resolveAppCommand(
          {
            action: () => {
              void router.push(
                queryParams.getHref(`/agents/${agent.id}`, orgSlug),
              )
            },
            description: "Open agent",
            group: "navigate",
            id: `entity-agent-${agent.id}`,
            keywords: ["agent", agent.id.toLowerCase()],
            label: `Agent: ${agent.display_name}`,
            scope: "global",
            searchOnly: true,
          },
          "entity",
        ),
      ),
      ...personas.map((persona) =>
        resolveAppCommand(
          {
            action: () => {
              void router.push(
                queryParams.getHref(`/personas?id=${persona.id}`, orgSlug),
              )
            },
            description: "Open persona",
            group: "navigate",
            id: `entity-persona-${persona.id}`,
            keywords: ["persona", persona.id.toLowerCase()],
            label: `Persona: ${persona.display_name}`,
            scope: "global",
            searchOnly: true,
          },
          "entity",
        ),
      ),
      ...templates.map((template) =>
        resolveAppCommand(
          {
            action: () => {
              void router.push(
                queryParams.getHref(`/templates/${template.id}`, orgSlug),
              )
            },
            description: "Open template",
            group: "navigate",
            id: `entity-template-${template.id}`,
            keywords: ["template", template.id.toLowerCase()],
            label: `Template: ${template.display_name}`,
            scope: "global",
            searchOnly: true,
          },
          "entity",
        ),
      ),
      ...reports.map((report) =>
        resolveAppCommand(
          {
            action: () => {
              void router.push(
                queryParams.getHref(`/reports/${report.ulid}`, orgSlug),
              )
            },
            description: "Open report",
            group: "navigate",
            id: `entity-report-${report.ulid}`,
            keywords: ["report", report.ulid.toLowerCase()],
            label: `Report: ${report.display_name}`,
            scope: "global",
            searchOnly: true,
          },
          "entity",
        ),
      ),
      ...alerts.map((alert: { id: string; name: string }) =>
        resolveAppCommand(
          {
            action: () => {
              void router.push(
                queryParams.getHref(`/alerts/${alert.id}`, orgSlug),
              )
            },
            description: "Open alert",
            group: "navigate",
            id: `entity-alert-${alert.id}`,
            keywords: ["alert", alert.id.toLowerCase()],
            label: `Alert: ${alert.name}`,
            scope: "global",
            searchOnly: true,
          },
          "entity",
        ),
      ),
    ]
  }, [
    agents,
    alertsQuery.data,
    orgSlug,
    personas,
    queryParams,
    reportsQuery.data,
    router,
    shouldLoadEntityCommands,
    templateData?.templates,
  ])

  const allCommands = useMemo(() => {
    const contextualCommands: ResolvedAppCommand[] = registeredCommands
      .filter((command) => command.available !== false)
      .map((command) => resolveAppCommand(command, "contextual"))

    const contextualCommandIds = new Set(
      contextualCommands.map((command) => command.id),
    )
    const navigationShortcutKeys = new Set(
      flattenedNavigationNodes
        .map((command) => command.key)
        .filter((key): key is string => !!key),
    )

    const shortcutCommands: ResolvedAppCommand[] = Array.from(
      shortcuts.values(),
    )
      .map((shortcut) => {
        const commandId =
          typeof shortcut.state?.commandId === "string"
            ? shortcut.state.commandId
            : `shortcut-${shortcut.key}`
        const paletteGroup =
          shortcut.state?.paletteGroup === "selection" ||
          shortcut.state?.paletteGroup === "create" ||
          shortcut.state?.paletteGroup === "navigate" ||
          shortcut.state?.paletteGroup === "other"
            ? shortcut.state.paletteGroup
            : undefined

        return resolveAppCommand(
          {
            action: shortcut.action,
            aliases: [shortcut.key.toLowerCase()],
            description:
              typeof shortcut.state?.description === "string"
                ? shortcut.state.description
                : undefined,
            group: paletteGroup,
            id: commandId,
            key: shortcut.key,
            keywords: [
              shortcut.label.toLowerCase(),
              shortcut.key.toLowerCase(),
            ],
            label: shortcut.label,
          },
          "shortcut",
        )
      })
      .filter(
        (shortcut) =>
          (shortcut.key
            ? shortcuts.get(shortcut.key)?.state?.hiddenInPalette
            : undefined) !== true,
      )
      .filter((shortcut) => !contextualCommandIds.has(shortcut.id))
      .filter(
        (shortcut) =>
          !shortcut.key || !navigationShortcutKeys.has(shortcut.key),
      )

    const systemCommands: ResolvedAppCommand[] = [
      resolveAppCommand(
        {
          action: dispatchOpenDebugInfo,
          aliases: ["debug", "diag", "status", "version"],
          description: "Open public app diagnostics.",
          group: "other",
          id: "system-open-debug-info",
          keywords: ["build", "commit", "environment", "timezone"],
          label: "Debug info",
          scope: "global",
        },
        "system",
      ),
    ]

    const mergedCommands = [
      ...contextualCommands,
      ...shortcutCommands,
      ...navigationCommands,
      ...entityCommands,
      ...systemCommands,
    ]
    const dedupedCommands = new Map<string, ResolvedAppCommand>()

    mergedCommands.forEach((command) => {
      if (!dedupedCommands.has(command.id)) {
        dedupedCommands.set(command.id, command)
      }
    })

    return Array.from(dedupedCommands.values())
  }, [
    flattenedNavigationNodes,
    entityCommands,
    navigationCommands,
    registeredCommands,
    shortcuts,
  ])

  return {
    commands: allCommands,
    globalNavigationShortcuts,
  }
}
